import React, { useState, useEffect, useRef, useCallback } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  alpha: number;
  color: string;
}

const CursorTrail: React.FC = () => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const nextParticleId = useRef(0);
  const cleanupTimers = useRef<NodeJS.Timeout[]>([]);

  const createParticle = useCallback((x: number, y: number) => {
    const id = nextParticleId.current++;
    const newParticle: Particle = {
      id,
      x: x + (Math.random() - 0.5) * 5,
      y: y + (Math.random() - 0.5) * 5,
      size: Math.random() * 8 + 4,
      alpha: 0.8,
      color: `rgba(0, 191, 255, 1)`,
    };

    setParticles((prevParticles) => [...prevParticles, newParticle]);

    const timer = setTimeout(() => {
      setParticles((prevParticles) => prevParticles.filter((p) => p.id !== id));
    }, 800);
    cleanupTimers.current.push(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      createParticle(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        createParticle(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      cleanupTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, [createParticle]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[999]">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full bg-cyan-400"
          style={{
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            opacity: p.alpha,
            transform: 'translate(-50%, -50%) scale(1)',
            animation: 'particle-fade-out 0.8s forwards',
            boxShadow: '0 0 15px 8px rgba(0, 191, 255, 0.8)',
          }}
        />
      ))}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes particle-fade-out {
          0% {
            opacity: 0.8;
            transform: translate(-50%, -50%) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0);
          }
        }
      ` }} />
    </div>
  );
};

export default CursorTrail;
